// Pure unit metadata: keep page/UI dependencies out of the critical route graph.
function stripHtml(html: string) {
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

export function buildProductJsonLd(rv: any, url: string) {
  const displayPrice = rv.sale_price ?? rv.price;
  const plainDesc = rv.description ? stripHtml(rv.description).slice(0, 5000) : `${rv.year} ${rv.make} ${rv.model} for sale`;

  const imageObjects = (rv.images ?? []).map((imgUrl: string, i: number) => ({
    "@type": "ImageObject",
    url: imgUrl,
    name: `${rv.title} Image Object ${i}`,
    "@id": imgUrl,
  }));

  const vehicle: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "Vehicle",
    "@id": url,
    url,
    name: rv.title,
    sku: rv.stock_number || undefined,
    vehicleIdentificationNumber: rv.vin || undefined,
    description: plainDesc,
    itemCondition: rv.condition === "new" ? "https://schema.org/NewCondition" : "https://schema.org/UsedCondition",
    manufacturer: rv.make ? {
      "@type": "Organization",
      name: rv.make,
      "@id": `https://www.rexandsonsrvs.com/#manufacturer-${(rv.make || "").toLowerCase().replace(/\s+/g, "-")}`,
    } : undefined,
    brand: rv.model ? { "@type": "Brand", name: rv.model } : undefined,
    model: rv.trim || undefined,
    bodyType: rv.rv_type || undefined,
    color: rv.exterior_color || undefined,
    modelDate: rv.year?.toString(),
    vehicleModelDate: rv.year?.toString(),
    image: imageObjects.length ? imageObjects : undefined,
    offers: displayPrice ? {
      "@type": "Offer",
      name: `${rv.title} Offer`,
      availability: "https://schema.org/InStock",
      itemOffered: { "@type": "Thing", name: url },
      priceCurrency: "USD",
      price: displayPrice.toFixed(2),
      itemCondition: rv.condition === "new" ? "https://schema.org/NewCondition" : "https://schema.org/UsedCondition",
      offeredBy: {
        "@type": "Organization",
        name: "Rex & Sons RVs",
        additionalType: "https://en.wikipedia.org/wiki/Recreational_vehicle",
        naics: "441210",
        telephone: "910-793-2894",
        areaServed: "https://en.wikipedia.org/wiki/United_States",
        address: {
          "@type": "PostalAddress",
          addressCountry: { "@type": "Country", name: "US" },
          addressLocality: "Castle Hayne",
          addressRegion: "NC",
          postalCode: "28429",
        },
        url: "https://www.rexandsonsrvs.com/",
        "@id": "https://www.rexandsonsrvs.com/",
      },
      url: `${url}#Offer01`,
      "@id": `${url}#Offer01`,
      hasMerchantReturnPolicy: {
        "@type": "MerchantReturnPolicy",
        applicableCountry: "US",
        returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
      },
      shippingDetails: {
        "@type": "OfferShippingDetails",
        shippingDestination: { "@type": "DefinedRegion", addressCountry: "US" },
        shippingRate: { "@type": "MonetaryAmount", value: 0, currency: "USD" },
        deliveryTime: {
          "@type": "ShippingDeliveryTime",
          handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 0, unitCode: "DAY" },
          transitTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 0, unitCode: "DAY" },
        },
      },
    } : undefined,
    weightTotal: rv.weight_lbs ? {
      "@type": "QuantitativeValue",
      value: rv.weight_lbs,
      unitCode: "LBR",
      name: `${rv.title} Weight Total`,
      "@id": `${url}#WeightTotal`,
    } : undefined,
    fuelType: rv.fuel_type || undefined,
    vehicleConfiguration: rv.rv_type,
    mileageFromOdometer: rv.mileage ? { "@type": "QuantitativeValue", value: rv.mileage, unitCode: "SMI" } : undefined,
  };

  return vehicle;
}

export function buildBreadcrumbJsonLd(rv: any, url: string) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://www.rexandsonsrvs.com/" },
      { "@type": "ListItem", position: 2, name: "Inventory", item: "https://www.rexandsonsrvs.com/inventory" },
      { "@type": "ListItem", position: 3, name: rv.title, item: url },
    ],
  };
}

